/**
 * MadrassaTeacherInvitations — page pour le madrassa_admin permettant de
 * créer des liens d'invitation pour des professeurs (qui s'inscriront sans
 * passer par la file de validation).
 *
 * Route: /madrassa-admin/teacher-invitations?madrassaId=...
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Send, Copy, Check, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const MadrassaTeacherInvitationsPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const madrassaId = params.get('madrassaId');
  const { user, loading: authLoading } = useAuth();

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!madrassaId) { navigate('/'); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, madrassaId]);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/madrassa-admin/teacher-invitations`, {
        params: { madrassa_id: madrassaId }, headers: headers(),
      });
      setInvites(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors du chargement.");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/madrassa-admin/teacher-invitations`,
        { madrassa_id: madrassaId, email },
        { headers: headers() });
      setEmail('');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de la création.");
    } finally { setBusy(false); }
  };

  const revoke = async (token) => {
    try {
      await axios.delete(`${API_URL}/api/onboarding/madrassa-admin/teacher-invitations/${token}`,
        { headers: headers() });
      await refresh();
    } catch (err) { setError(err?.response?.data?.detail || "Erreur."); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/register?invite=${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 1500);
  };

  if (authLoading || loading) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28" data-testid="teacher-invitations-page">
      <Link to={`/admin/madrassas/${madrassaId}`}
            className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-6 text-sm">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour à la madrassa
      </Link>

      <SectionHeader title="Inviter un professeur"
                     subtitle="Envoyez un lien personnel — l'invité s'inscrit en un clic, sans validation supplémentaire." />

      <GlassCard className="my-6 p-5">
        <form onSubmit={submit} className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[200px] space-y-2">
            <Label htmlFor="invite-email" className="text-text-primary">Email du professeur</Label>
            <Input id="invite-email" type="email" required
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="prof@example.com"
                   className="bg-surface border-border"
                   data-testid="invite-email-input" />
          </div>
          <Button type="submit" disabled={busy} className="bg-primary text-background hover:bg-primary-hover"
                  data-testid="invite-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Envoyer l'invitation
          </Button>
        </form>
        {error && <p className="text-danger text-sm mt-3" data-testid="invite-error">{error}</p>}
      </GlassCard>

      <h3 className="text-sm uppercase tracking-wider text-text-muted mb-3">
        Invitations actives ({invites.filter(i => i.status === 'pending').length})
      </h3>

      {invites.length === 0 ? (
        <GlassCard className="text-center py-12">
          <Mail className="w-10 h-10 text-text-muted mx-auto mb-2" />
          <p className="text-text-secondary text-sm">Aucune invitation envoyée pour l'instant.</p>
        </GlassCard>
      ) : (
        <div className="space-y-2" data-testid="invitations-list">
          {invites.map((inv) => (
            <GlassCard key={inv.token} className="p-4" data-testid={`invitation-row-${inv.token}`}>
              <div className="flex flex-wrap gap-3 justify-between items-center">
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">{inv.email}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    Créée le {new Date(inv.created_at).toLocaleString('fr-FR')} • expire le {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                {inv.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)}
                            data-testid={`invitation-copy-${inv.token}`}>
                      {copied === inv.token ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                      {copied === inv.token ? 'Copié' : 'Copier le lien'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => revoke(inv.token)}
                            className="text-rose-400 hover:text-rose-500 hover:bg-rose-500/10"
                            data-testid={`invitation-revoke-${inv.token}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const meta = {
    pending:  { fr: 'Active',     cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    consumed: { fr: 'Acceptée',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    revoked:  { fr: 'Révoquée',   cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  }[status] || { fr: status, cls: 'bg-surface text-text-muted border-border' };
  return <Badge className={`${meta.cls} border text-[10px]`}>{meta.fr}</Badge>;
};

export default MadrassaTeacherInvitationsPage;
